<script setup lang="ts">
/**
 * A gate waiting on a human, written out (apps/portal/src/api.ts GateRow): who is asking, what approving
 * it does, why it stopped, exactly what goes out, and how long it has waited — with the portal's action
 * state in its words. The action tone is on its edge, never a fill (BG §18.4).
 *
 * Two layouts. `card` is the decision on its own, as Gates and the Control page list them. `inline` is the
 * same decision raised in a thread, as the chat shows it: a kicker and the state on one line, then what
 * and why. The decision's controls come through the `actions` slot; `drawn` marks them as a picture (the
 * site) rather than controls (the portal), and hides them from a screen reader.
 *
 * For the portal's live cards: `from` and `title` slots take links in place of the words, `body` adds
 * what the summary could not say (that there is none), and `statusTone` marks a decision that has waited
 * too long in the failed tone. The card carries that tone as `data-tone` while it waits, and a failed one
 * takes its edge too, so a screen never reaches into the card to paint it.
 *
 * `decided` is the decision just made on it. The card says so in that decision's tone, on its state and its
 * edge (in `decidedLabel`'s words when the gate offered its own choices), flashes a ring of it, and fades: gone after MOTION.decisionMs, which is how long the caller keeps it
 * on the page before taking it off. A list of these cards that names its TransitionGroup `zh-decision` has the
 * cards after a decided one slide into its place once it has gone, while motion is allowed.
 *
 * `settled` is how the gate ended, kept on the card for good, as a chat thread keeps a decided gate where it was
 * raised. The card says it on its state (in `decidedLabel`'s words when there are any) and takes its tone on the
 * edge, from the same outcome map `decided` uses (an answer in the approved tone, a gate that ended some other way
 * in a neutral one). It never fades or leaves, renders no controls, heads itself with when it was decided
 * (`settledWhen`) and shows `settledLines` (the note, or each answer) under its title. A card already settled when
 * it mounts does not move; one that becomes
 * settled while on the page flashes the decided ring once (`data-settling`), unless motion is reduced.
 *
 * `kind` says what deciding it does (#1760): an `action` runs something when it is approved, a `question` is answered
 * and only informs the run. The card says which in words ("Approve an action" / "Answer a question", the inline
 * layout's kicker), carries it as `data-kind`, and takes the kind's own edge tone from the theme
 * (`--zh-decision-action` / `--zh-decision-question`) while it waits. Given no kind, the card is as it always was.
 *
 * The `details` slot is the decision's reasoning, folded under "Show details" (ZhDetailsFold) and closed until the
 * reader opens it. The body and the controls are never in the fold: what is decided on and how stay in plain view.
 * The fold is reasoning, not a control, so a settled card keeps it in both layouts: why it was decided stays one
 * press away after the decision.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import type { DecisionKind, DecisionOutcome, DecisionView, SettledOutcome } from '../lib/surfaces'
import type { StatusTone } from '../tokens'
import ZhDecisionSections from './ZhDecisionSections.vue'
import ZhDetailsFold from './ZhDetailsFold.vue'
import ZhStatusPill from './ZhStatusPill.vue'

const props = withDefaults(
    defineProps<{
        decision: DecisionView
        variant?: 'card' | 'inline'
        /** The state in the portal's words. */
        status?: string
        /** The state's tone: waiting on you, unless it has waited too long. */
        statusTone?: StatusTone
        /** The inline layout's kicker while it waits. A settled card says how long ago it was decided there instead. */
        kicker?: string
        drawn?: boolean
        /** The decision just made on it, shown until the card has faded. */
        decided?: DecisionOutcome
        /** The choice it was decided through, in its button's words, said in place of Approved or Rejected. */
        decidedLabel?: string
        /** The decision it was settled on, kept on the card for good: no fade, no removal, no controls. */
        settled?: SettledOutcome
        /** The lines a settled card shows under its title: the note, or each answer. */
        settledLines?: string[]
        /** When it was settled, in words. */
        settledWhen?: string
        /** What deciding it does: an action runs something, a question is answered. None leaves the card unmarked. */
        kind?: DecisionKind
    }>(),
    { variant: 'card', status: 'Waiting for your decision', statusTone: 'action', kicker: 'Needs you', drawn: false },
)

/** The kind in the card's words: what the person is asked to do with it. */
const KIND_LABEL: Record<DecisionKind, string> = {
    action: 'Approve an action',
    question: 'Answer a question',
}

const kindLabel = computed(() => (props.kind ? KIND_LABEL[props.kind] : undefined))

/**
 * A settled card no longer needs anybody, so its head says it was decided (or ended, without a decision) and when,
 * where a waiting card says who it needs or how long it has waited.
 */
const settledHead = computed(() => {
    const what = props.settled === 'closed' ? 'Ended' : 'Decided'
    return props.settledWhen ? `${what} ${props.settledWhen}` : what
})

/** Approved finished well, Rejected ended it, and Request changes sends the work back into motion (BG §18.4). */
const DECIDED: Record<DecisionOutcome, { status: string; tone: StatusTone }> = {
    approve: { status: 'Approved', tone: 'completed' },
    reject: { status: 'Rejected', tone: 'failed' },
    request_changes: { status: 'Changes requested', tone: 'running' },
}

/** How a gate ended, for good: the decisions as above, an answer as an approval, any other end in a neutral tone. */
const SETTLED: Record<SettledOutcome, { status: string; tone: StatusTone }> = {
    ...DECIDED,
    answered: { status: 'Answered', tone: 'completed' },
    closed: { status: 'Closed', tone: 'neutral' },
}

/** How long the decided ring takes to flash out (`zh-decision-ring` below). */
const RING_MS = 500

const state = computed(() => {
    if (props.settled) {
        return { ...SETTLED[props.settled], status: props.decidedLabel ?? SETTLED[props.settled].status }
    }
    if (props.decided) {
        return { ...DECIDED[props.decided], status: props.decidedLabel ?? DECIDED[props.decided].status }
    }
    return { status: props.status, tone: props.statusTone }
})

/** What the card carries as `data-decided`: how it ended for good, else the decision just made on it. */
const outcome = computed(() => props.settled ?? props.decided)

/** The settled card's lines under its title: what was said with the decision. */
const settledShown = computed(() => !!props.settled && !!props.settledLines?.length)

/** The card became settled while on the page and its ring is flashing. Never for a card settled when it mounts. */
const settling = ref(false)
let settlingTimer: ReturnType<typeof setTimeout> | undefined

/** Someone who asked for less motion sees the outcome at once, with no ring. */
function motionAllowed(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false
    }
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

watch(
    () => props.settled,
    (now, before) => {
        if (!now || before || !motionAllowed()) {
            return
        }
        settling.value = true
        clearTimeout(settlingTimer)
        settlingTimer = setTimeout(() => {
            settling.value = false
        }, RING_MS)
    },
)

onBeforeUnmount(() => {
    clearTimeout(settlingTimer)
})
</script>

<template>
    <article
        class="zh-decision"
        :data-variant="variant"
        :data-decided="outcome"
        :data-settled="settled ? 'true' : undefined"
        :data-settling="settled && settling ? 'true' : undefined"
        :data-tone="outcome ? undefined : statusTone"
        :data-kind="kind"
    >
        <template v-if="variant === 'card'">
            <p v-if="kindLabel" class="zh-decision__label zh-decision__kind">{{ kindLabel }}</p>
            <p class="zh-decision__top">
                <span class="zh-decision__label"
                    ><slot name="from"
                        ><span class="zh-decision__ink">{{ decision.from }}</span
                        ><template v-if="decision.by"> / {{ decision.by }}</template></slot
                    ></span
                >
                <span v-if="settled" class="zh-decision__label">{{ settledHead }}</span>
                <span v-else-if="decision.waited" class="zh-decision__label">Waiting {{ decision.waited }}</span>
            </p>
            <p class="zh-decision__title"><slot name="title">{{ decision.title }}</slot></p>
            <p v-if="decision.why" class="zh-decision__why">{{ decision.why }}</p>
            <ul v-if="decision.details?.length" class="zh-decision__what">
                <li v-for="line in decision.details" :key="line">{{ line }}</li>
            </ul>
            <ZhDecisionSections v-if="decision.sections?.length" :sections="decision.sections" />
            <slot name="body" />
            <ZhDetailsFold v-if="$slots.details"><slot name="details" /></ZhDetailsFold>
            <ul v-if="settledShown" class="zh-decision__settled">
                <li v-for="(line, index) in settledLines" :key="index">{{ line }}</li>
            </ul>
            <p class="zh-decision__state"><ZhStatusPill :tone="state.tone">{{ state.status }}</ZhStatusPill></p>
        </template>
        <template v-else>
            <p class="zh-decision__row">
                <span class="zh-decision__label">{{ settled ? settledHead : (kindLabel ?? kicker) }}</span>
                <ZhStatusPill :tone="state.tone">{{ state.status }}</ZhStatusPill>
            </p>
            <p class="zh-decision__title">{{ decision.title }}</p>
            <p v-if="decision.why" class="zh-decision__label">{{ decision.why }}</p>
            <ZhDetailsFold v-if="$slots.details"><slot name="details" /></ZhDetailsFold>
            <ul v-if="settledShown" class="zh-decision__settled">
                <li v-for="(line, index) in settledLines" :key="index">{{ line }}</li>
            </ul>
        </template>
        <div v-if="$slots.actions && !settled" class="zh-decision__actions" :aria-hidden="drawn ? 'true' : undefined">
            <slot name="actions" :decision="decision" />
        </div>
    </article>
</template>

<style scoped>
p,
ul {
    margin: 0;
}

ul {
    padding: 0;
    list-style: none;
}

/* The system label (BG §19.3), set here so the card carries it wherever it is used. */
.zh-decision__label {
    margin: 0;
    color: var(--ink-3);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.4;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
}

.zh-decision__ink {
    color: var(--ink-1);
}

.zh-decision__title {
    color: var(--ink-1);
    font-weight: 600;
}

/* ── card: the decision on its own ─────────────────────────────────────────────────────── */

.zh-decision[data-variant="card"] {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1.125rem 1.25rem;
    border: 1px solid color-mix(in srgb, var(--state-action) 55%, transparent);
    background: color-mix(in srgb, var(--state-action) 4%, var(--panel));
}

.zh-decision__top {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 0.25rem 1rem;
}

.zh-decision[data-variant="card"] .zh-decision__title {
    font-size: 1.1875rem;
    line-height: 1.3;
}

.zh-decision__why {
    color: var(--ink-1);
    font-size: 0.9375rem;
}

.zh-decision__what {
    display: grid;
    gap: 0.25rem;
    padding: 0.625rem 0 0.25rem;
    border-top: 1px solid var(--hair);
    color: var(--ink-2);
    font-size: 0.9375rem;
}

.zh-decision__what li::before {
    content: "–\00a0" / "";
    color: var(--ink-3);
}

.zh-decision__state {
    margin-top: auto;
    padding-top: 0.5rem;
}

.zh-decision[data-variant="card"] .zh-decision__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
}

/*
 * ── inline: the same decision, raised in a thread ───────────────────────────────────────
 * As wide as the thread's messages where a page widens them (`--zh-chat-bubble-max`, ZhChatThread), else 30rem.
 */

.zh-decision[data-variant="inline"] {
    display: grid;
    gap: 0.375rem;
    max-inline-size: min(var(--zh-chat-bubble-max, 30rem), 100%);
    padding: 0.875rem 1rem;
    border: 1px solid color-mix(in srgb, var(--state-action) 60%, transparent);
    background: var(--panel-2);
}

.zh-decision[data-variant="inline"] .zh-decision__title {
    font-size: 1rem;
}

.zh-decision__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
}

.zh-decision[data-variant="inline"] .zh-decision__actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

/* ── kind: an action to approve, or a question to answer ───────────────────────────────── */

/*
 * The two kinds look different at a glance (#1760): an action, whose approval runs something, keeps the action
 * tone on its edge; a question, which is only answered, takes the quieter strong line, on a plain panel. Each
 * from its own theme token, so a screen never paints one. Placed before the aged and decided rules, which win.
 */
.zh-decision[data-kind="action"] {
    border-color: color-mix(in srgb, var(--zh-decision-action) 60%, transparent);
}

.zh-decision[data-kind="question"] {
    border-color: var(--zh-decision-question);
}

.zh-decision[data-variant="card"][data-kind="question"] {
    background: var(--panel);
}

.zh-decision__kind {
    color: var(--ink-2);
}

/* ── waited too long: the failed tone on the edge as well as the state ─────────────────── */

/*
 * A decision that has waited too long is an issue now, not only a wait (Gates' aged gate), so its edge takes
 * the failed tone with its state. Only while it waits: a decided card shows its decision's edge instead.
 */
.zh-decision[data-tone="failed"]:not([data-decided]) {
    border-color: color-mix(in srgb, var(--state-failed) 60%, transparent);
}

/* ── decided: the decision just made, for the moment before the card leaves ───────────── */

/*
 * The decision's tone takes the edge, a ring of it flashes out, then the card fades (approved lifts away,
 * the others drop) and is gone by --motion-decision. It takes no more clicks while it goes.
 */
.zh-decision[data-decided="approve"] {
    --zh-decided: var(--state-complete);
    --zh-decided-drift: -0.5rem;
}

.zh-decision[data-decided="reject"] {
    --zh-decided: var(--state-failed);
    --zh-decided-drift: 0.5rem;
}

.zh-decision[data-decided="request_changes"] {
    --zh-decided: var(--state-active);
    --zh-decided-drift: 0.5rem;
}

/* A question answered is an approval on Gates, so it takes the approved edge; its state says "Answered". */
.zh-decision[data-decided="answered"] {
    --zh-decided: var(--state-complete);
}

/* A gate that ended without a decision (timed out, cancelled with its run): neutral, its state in words. */
.zh-decision[data-decided="closed"] {
    --zh-decided: var(--state-inactive);
}

.zh-decision[data-decided] {
    border-color: var(--zh-decided);
}

/* Only the moment a decision is made fades and leaves: a settled card stays, and stays readable. */
.zh-decision[data-decided]:not([data-settled]) {
    pointer-events: none;
    animation: zh-decided-fade 250ms ease-in calc(var(--motion-decision) - 250ms) forwards;
}

@media (prefers-reduced-motion: no-preference) {
    .zh-decision[data-decided]:not([data-settled]) {
        animation:
            zh-decision-ring 500ms ease-out,
            zh-decided-leave 250ms ease-in calc(var(--motion-decision) - 250ms) forwards;
    }

    /* A card settled while on the page flashes the same ring once, and nothing else: it neither fades nor moves. */
    .zh-decision[data-settling] {
        animation: zh-decision-ring 500ms ease-out;
    }
}

/* ── settled: what was said with the decision ──────────────────────────────────────────── */

.zh-decision__settled {
    display: grid;
    gap: 0.125rem;
    padding-top: 0.375rem;
    border-top: 1px solid var(--hair);
    color: var(--ink-2);
    font-size: 0.875rem;
    overflow-wrap: anywhere;
}

@keyframes zh-decision-ring {
    from {
        box-shadow: 0 0 0 0 color-mix(in srgb, var(--zh-decided) 60%, transparent);
    }
    to {
        box-shadow: 0 0 0 0.75rem transparent;
    }
}

@keyframes zh-decided-fade {
    to {
        opacity: 0;
    }
}

@keyframes zh-decided-leave {
    to {
        opacity: 0;
        transform: translateY(var(--zh-decided-drift)) scale(0.98);
    }
}
</style>

<!--
    A queue of these cards (a TransitionGroup named `zh-decision`, as Gates lists them) closes up behind one that
    has gone. Not scoped: what moves is the caller's list item, not the card. The fade stays on the card, never
    the item, because TransitionGroup skips the slide when the first item carries an animation of its own.
-->
<style>
@media (prefers-reduced-motion: no-preference) {
    .zh-decision-move {
        transition: transform 300ms ease-out;
    }
}
</style>
